import { useState, useRef, useCallback, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { get, isEmpty, isEqual } from "lodash";
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  Controls,
  Background,
  applyEdgeChanges,
  applyNodeChanges,
} from "reactflow";
import { useDispatch, useSelector } from "react-redux";
import "reactflow/dist/style.css";
import ResizableNode from "../ResizableNode";
import "@reactflow/node-resizer/dist/style.css";
import "reactflow/dist/base.css";
import {
  UNDEFINED,
  INDEX,
  EMPTY_STRING,
  HANDLE_TYPES,
  EMPTY_ARRAY,
  ZERO,
  ONE,
  WINDOW_EVENTS,
  KEYDOWN,
} from "../../../constants";
import { actions } from "../../../pages/DataModels/dataModels.reducers";
import { actions as modalActions } from "../../organisms/Modal/modal.reducers";
import {
  CUSTOM,
  FIELDS,
  ROOT_NODE,
} from "../../../pages/DataModels/datamodels.constants";
import Spinner, { SPINNER_NAMES } from "../../organisms/Spinner";
import Alert, { selectAlert } from "../../organisms/Alert";
import ReactFlowToolbar from "./ReactFlowToolbar";
import { chevronLeftIcon, chevronRightIcon } from "../../../assets/icons";

const { setSelectedNode } = actions;
const { setSelectedModal } = modalActions;
const { FETCH_SERVICE } = SPINNER_NAMES;

const VARIANT = "lines";
const GAP_VALUE = 7;
const NODE_DATA_TRANSFER_KEY = "application/reactflow";
const MOVE = "move";
const REMOVE = "remove";
const TOOLTIP_MESSAGE_TIMEOUT = 3000;
const CONNECTION_RADIUS = 0;
const { FIRST, SECOND, THIRD } = INDEX;
const { VALUE, TRIGGER } = HANDLE_TYPES;
const { LOGICAL, OUTPUTS } = FIELDS;
const INTERNAL_OUTPUT = "internalOutput";
const INTERNAL_INPUT = "internalInput";
const SUB_PARENT = "subParent";
const PARENT = "parent";
const initialCopiedFlow = { nodes: EMPTY_ARRAY, edges: EMPTY_ARRAY };
const { COPY, PASTE, CLICK } = WINDOW_EVENTS;
const DEFAULT_DELETE_KEY = "Backspace";
const undoKey = 90;
const redoKey = 89;
const DEFAULT_DRAWER_WIDTH = 300;

const nodeTypes = {
  resize: ResizableNode,
};

const ReactFlowEditor = (props) => {
  const {
    nodes,
    edges,
    setElements,
    undo,
    redo,
    canUndo,
    canRedo,
    checkValidation,
    isFlowValidated,
    removeHandle,
    updateNode,
    isViewOnlyMode,
    setIsViewOnlyMode,
    setShowLeftSidebarButton,
    showLeftSidebarButton,
    setRightDrawerWidth,
    setLeftDrawerWidth,
    setShowRightSidebarButton,
    showRightSidebarButton,
    handleMouseMove,
  } = props;
  const dispatch = useDispatch();
  const selectedAlert = useSelector(selectAlert);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [alertData, setAlertData] = useState(selectedAlert);
  const [tooltipMessage, setTooltipMessage] = useState(EMPTY_STRING);
  const [copiedFlow, setCopiedFlow] = useState(initialCopiedFlow);
  const [selectedFlow, setSelectedFlow] = useState(initialCopiedFlow);
  const [isPageFocused, setIsPageFocused] = useState(false);
  const reactFlowWrapper = useRef(null);
  const customHandleParams = useRef();
  const clearTooltipTimeoutRef = useRef();
  const componentRef = useRef();
  const proOptions = { hideAttribution: true };

  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  useEffect(() => {
    if (tooltipMessage) {
      clearTooltipTimeoutRef.current &&
        clearTimeout(clearTooltipTimeoutRef.current);

      clearTooltipTimeoutRef.current = setTimeout(
        () => setTooltipMessage(),
        TOOLTIP_MESSAGE_TIMEOUT
      );
    }
  }, [tooltipMessage]);

  const displayViewOnlyMessage = useCallback(
    () => setTooltipMessage("View Only Mode"),
    []
  );

  const triggerElementsUpdate = useCallback(
    ({ updatedNodes, updatedEdges }) => {
      setElements({
        nodes: updatedNodes || nodes,
        edges: updatedEdges || edges,
      });
    },
    [setElements, nodes, edges]
  );

  const onNodesChange = useCallback(
    (changes) => {
      const updatedNodes = applyNodeChanges(changes, nodes);
      triggerElementsUpdate({ updatedNodes });
      checkValidation(nodes, edges);
    },
    [nodes, triggerElementsUpdate, checkValidation, edges]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      const updatedEdges = applyEdgeChanges(changes, edges);
      triggerElementsUpdate({ updatedEdges });
      checkValidation(nodes, edges);
    },
    [edges, triggerElementsUpdate, checkValidation, nodes]
  );

  const checkParentNodeExists = useCallback(
    () => nodes.some(({ data }) => data.isParent),
    [nodes]
  );

  const getChildNodeProperties = useCallback(
    (position) => {
      const data = nodes
        .filter(({ data }) => data.isParent)
        .filter((node) => {
          const currentParentNodePosition = node.parentNode
            ? node.positionAbsolute
            : node.position;
          const isInsideXAxis =
            position.x > currentParentNodePosition.x &&
            position.x < currentParentNodePosition.x + node.width;
          const isInsideYAxis =
            position.y > currentParentNodePosition.y &&
            position.y < currentParentNodePosition.y + node.height;
          return isInsideXAxis && isInsideYAxis;
        });

      if (isEmpty(data)) return;
      let subParentNode = data.filter((node) => node.data.isSubParent);
      subParentNode = subParentNode[subParentNode.length - 1];
      const parentNode = subParentNode || data[FIRST];
      const parentNodePosition = subParentNode
        ? parentNode.positionAbsolute
        : parentNode.position;
      const xAxisPosition = position.x - parentNodePosition.x;
      const yAxisPosition = position.y - parentNodePosition.y;
      const newPosition = { x: xAxisPosition, y: yAxisPosition };
      return {
        id: parentNode.id,
        parentName: parentNode.data.name,
        position: newPosition,
        parentType: subParentNode ? SUB_PARENT : PARENT,
      };
    },
    [nodes]
  );

  const onDragOver = useCallback(
    (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = MOVE;
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
      const shouldHighlightParentNode = getChildNodeProperties(position);
      const updatedNodes = nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          highlight:
            shouldHighlightParentNode &&
            shouldHighlightParentNode.id === node.id,
        },
      }));
      setElements({ nodes: updatedNodes });
    },
    [reactFlowInstance, getChildNodeProperties, setElements, nodes]
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const nodeData = JSON.parse(
        event.dataTransfer.getData(NODE_DATA_TRANSFER_KEY)
      );

      // check if the dropped element is valid
      if (typeof nodeData === UNDEFINED || !nodeData) {
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const doesParentNodeExists = checkParentNodeExists();
      let parentId;
      if (doesParentNodeExists) parentId = getChildNodeProperties(position);

      const isSubParent = parentId && nodeData.card.isParent;

      const { card } = nodeData;
      const id = uuid();
      let newNode;

      if (checkParentNodeExists() && parentId) {
        newNode = {
          ...ROOT_NODE,
          id,
          position: parentId.position,
          positionAbsolute: position,
          deletable: true,
          parentNode: parentId.id,
          expandParent: true,
          extent: "parent",
          zIndex: isSubParent ? -5 : 0,
          isSubParent: isSubParent,
          data: {
            ...card,
            id,
            isValidated: Boolean(card.isValidated),
            parentNode: parentId.id,
            isSubParent: isSubParent,
            isSubChild: parentId.parentType === SUB_PARENT,
          },
        };
      } else {
        newNode = {
          ...ROOT_NODE,
          id,
          position,
          deletable: true,
          zIndex: card.isParent ? -10 : 0,
          data: {
            ...card,
            id,
            isValidated: !!card.isValidated,
          },
        };
      }
      setElements({
        nodes: parentId
          ? nodes
              .map((node) => ({
                ...node,
                data: {
                  ...node.data,
                  highlight: false,
                },
              }))
              .concat(newNode)
          : nodes.concat(newNode),
      });
    },
    [
      reactFlowInstance,
      setElements,
      checkParentNodeExists,
      getChildNodeProperties,
      nodes,
    ]
  );

  const createDynamicHandle = useCallback(
    async (params, uid, sourceHandle, count, connectPosition) => {
      const { name, type } = sourceHandle;
      const updatedNodes = await nodes.map((node) => {
        if (node.id === params.target) {
          const handle = {
            name: `${name}${count === FIRST ? EMPTY_STRING : count}`,
            key: `${uid}`,
            type: type,
            custom: true,
            connectPosition,
          };

          const updatedHandles = node.data.inputs.map((input, index) => ({
            ...input,
            connectPosition:
              (node.width / node.data.inputs.length) * index +
              node.width / node.data.inputs.length / THIRD,
          }));

          updatedHandles.push(handle);
          updatedHandles.sort((a, b) => a.connectPosition - b.connectPosition);

          return {
            ...node,
            data: {
              ...node.data,
              inputs: updatedHandles,
            },
          };
        } else return node;
      });
      return updatedNodes;
    },
    [nodes]
  );

  const onConnect = useCallback(
    (params) => (customHandleParams.current = params),
    []
  );

  const removeEdge = useCallback(
    (changes, id) => {
      if (isViewOnlyMode) return displayViewOnlyMessage();
      const edgeData = edges.find((edge) => edge.id === id);
      const updatedEdges = applyEdgeChanges(changes, edges);
      const targetNodeId = edgeData.target;
      const targetHandleId = edgeData.targetHandle;
      const targetHandleEdges = updatedEdges.find(
        (edge) =>
          targetNodeId === edge.target && targetHandleId === edge.targetHandle
      );
      const handlesToBeDeleted = [];
      let updatedNodes;

      if (!targetHandleEdges) {
        updatedNodes = nodes.map((node) => {
          if (targetNodeId === node.id) {
            const updatedInputs = node.data.inputs.filter(
              (handle) =>
                !handle.custom || targetHandleId !== `tava_${handle.key}`
            );
            if (node.data.group === LOGICAL) {
              const inputKey = targetHandleId.split("_").pop();
              const outputHandle = node.data.outputs.filter((output) =>
                output.key.includes(inputKey)
              );
              outputHandle.length &&
                outputHandle.forEach((handle) =>
                  handlesToBeDeleted.push({
                    nodeId: node.id,
                    handleId: handle.key,
                    type: OUTPUTS,
                  })
                );
            }
            return {
              ...node,
              data: {
                ...node.data,
                inputs: updatedInputs,
              },
              selected: false,
            };
          } else return node;
        });
      }

      let elements = { updatedNodes, updatedEdges };

      if (handlesToBeDeleted.length) {
        elements = handlesToBeDeleted.reduce(
          (updatedElements, handle) =>
            removeHandle({ ...handle, ...updatedElements }),
          elements
        );
      }
      triggerElementsUpdate(elements);
    },
    [
      nodes,
      edges,
      triggerElementsUpdate,
      removeHandle,
      isViewOnlyMode,
      displayViewOnlyMessage,
    ]
  );

  const onConnectEnd = useCallback(
    (event) => {
      const params = customHandleParams.current;
      customHandleParams.current = EMPTY_STRING;

      if (params) {
        const { targetHandle, sourceHandle, source, target } = params;
        const targetHandleData = targetHandle.split("_");
        const sourceHandleData = sourceHandle.split(/_(.*)/);
        const parentData = nodes.find((node) => node.id === source).data;

        if (source !== target) {
          const handleKey = targetHandleData[SECOND];
          const sourceKey = sourceHandleData[SECOND];
          const handleName = targetHandleData[THIRD];

          if (handleName === CUSTOM) {
            const sourceNode = parentData.outputs.find(
              (output) => output.key === sourceKey
            );
            const sourceName = sourceNode.name;
            const count = nodes.reduce((accumulator, node) => {
              if (node.id === target) {
                const inputs = get(node, "data.inputs");
                const data = inputs.filter((input) =>
                  input.name.includes(sourceName)
                );
                return accumulator + data.length;
              }
              return accumulator;
            }, FIRST);
            params.targetHandle = `tava_${handleKey}`;
            createDynamicHandle(
              params,
              handleKey,
              sourceNode,
              count,
              event.offsetX
            ).then((nodes) => {
              const updatedEdges = addEdge(params, edges);
              setElements({ nodes, edges: updatedEdges });
            });
          } else {
            const updatedEdges = addEdge(params, edges);
            setElements({ edges: updatedEdges });
          }
        }
      }
    },
    [createDynamicHandle, edges, nodes, setElements]
  );

  const removeNode = useCallback(
    (nodesId) => {
      const handlesToBeDeleted = [];
      const updatedEdges = edges.filter(
        (edge) =>
          !(nodesId.includes(edge.source) || nodesId.includes(edge.target))
      );
      const updatedNodes = nodes
        .filter((node) => !nodesId.includes(node.id))
        .map((node) => updateNode(node, updatedEdges, handlesToBeDeleted));

      let elements = { updatedNodes, updatedEdges };

      if (handlesToBeDeleted.length) {
        elements = handlesToBeDeleted.reduce(
          (updatedElements, handle) =>
            removeHandle({ ...handle, ...updatedElements }),
          elements
        );
      }
      triggerElementsUpdate(elements);
    },
    [edges, nodes, triggerElementsUpdate, removeHandle, updateNode]
  );

  const isValidConnection = useCallback(
    (connection) => {
      if (isViewOnlyMode) return;
      const { targetHandle, sourceHandle, source, target } = connection;

      const edgeId = `reactflow__edge-${source}${sourceHandle}-${target}${targetHandle}`;
      const isConnectionExist = edges.some((edge) => edge.id === edgeId);
      if (isConnectionExist) return false;

      const targetKey = targetHandle.split("_").pop();
      const sourceKey = sourceHandle.split(/_(.*)/)[SECOND];
      const sourceNode = nodes.find((node) => node.id === source).data;
      const targetNode = nodes.find((node) => node.id === target).data;
      const sourceHandleData = sourceNode.outputs.find(
        (output) => output.key === sourceKey
      );
      const targetHandleData = targetNode.inputs.find(
        (input) => input.key === targetKey
      );

      if (targetKey === INTERNAL_INPUT) {
        if (sourceNode.parentNode !== target) return false;
      } else if (sourceNode.isParent) {
        const isConnectingToNonChild = targetNode.parentNode === source;
        if (sourceKey === INTERNAL_OUTPUT && !isConnectingToNonChild)
          return false;
        if (sourceKey !== INTERNAL_OUTPUT && isConnectingToNonChild)
          return false;
      } else if (
        targetNode.isParent &&
        !sourceNode.isParent &&
        targetKey === INTERNAL_INPUT
      )
        return false;
      else if (sourceNode.parentNode !== targetNode.parentNode) return false;
      const isCustomHandle = targetKey === CUSTOM;
      const isValidConnection = isCustomHandle
        ? !targetNode.restrictedInputTypes?.includes(sourceHandleData.type)
        : sourceHandleData.type === VALUE ||
          targetHandleData.type === TRIGGER ||
          sourceHandleData.type === targetHandleData.type ||
          (sourceHandleData.type !== TRIGGER &&
            targetHandleData.type === VALUE);

      return isValidConnection;
    },
    [nodes, edges, isViewOnlyMode]
  );

  const onInit = (ref) => {
    setReactFlowInstance(ref);
    ref.fitView();
  };

  const handleCopyNodes = useCallback(() => {
    if (!isPageFocused) return;
    if (isViewOnlyMode) return displayViewOnlyMessage();
    const selectedNodes = selectedFlow.nodes.filter(
      ({ data }) => data.nodeId !== ZERO
    );
    if (isEmpty(selectedNodes)) {
      setTooltipMessage("No nodes and edges copied");
      setCopiedFlow(initialCopiedFlow);
      return;
    }
    const selectedNodesId = selectedNodes.map(({ id }) => id);
    const selectedEdges = selectedFlow.edges.filter(
      ({ source, target }) =>
        selectedNodesId.includes(source) && selectedNodesId.includes(target)
    );
    const selectedEdgesId = selectedEdges.map(({ id }) => id);

    setCopiedFlow({ nodes: selectedNodes, edges: selectedEdges });
    setTooltipMessage(
      `Copied ${selectedNodes.length} nodes and ${selectedEdges.length} edges`
    );
    const updatedNodes = nodes.map((node) => ({
      ...node,
      selected: selectedNodesId.includes(node.id),
    }));
    const updatedEdges = edges.map((edge) => ({
      ...edge,
      selected: selectedEdgesId.includes(edge.id),
    }));
    triggerElementsUpdate({ updatedNodes, updatedEdges });
  }, [
    nodes,
    edges,
    triggerElementsUpdate,
    isPageFocused,
    selectedFlow,
    isViewOnlyMode,
    displayViewOnlyMessage,
  ]);

  const handlePasteNodes = useCallback(() => {
    if (!isPageFocused) return;
    if (isViewOnlyMode) return displayViewOnlyMessage();
    if (isEmpty(copiedFlow.nodes)) {
      setTooltipMessage("No flow copied");
      return;
    }
    let updatedNodesId = {};
    const updatedNewNodes = copiedFlow.nodes.map((node) => {
      const id = uuid();
      updatedNodesId[node.id] = id;
      const position = node.parentNode
        ? node.position
        : { x: node.position.x + 15, y: node.position.y + 15 };
      return {
        ...node,
        id,
        position,
        selected: true,
        data: { ...node.data, id },
      };
    });
    const updatedNewEdges = copiedFlow.edges.map((edge) => {
      const source = updatedNodesId[edge.source];
      const target = updatedNodesId[edge.target];
      const id = `reactflow__edge-${source}${edge.sourceHandle}-${target}${edge.targetHandle}`;
      return { ...edge, id, source, target, selected: true };
    });

    const updatedCurrentNodes = nodes.map((node) => ({
      ...node,
      selected: false,
    }));
    const updatedCurrentEdges = edges.map((edge) => ({
      ...edge,
      selected: false,
    }));

    triggerElementsUpdate({
      updatedNodes: [...updatedCurrentNodes, ...updatedNewNodes],
      updatedEdges: [...updatedCurrentEdges, ...updatedNewEdges],
    });
    setTooltipMessage(
      `Pasted ${updatedNewNodes.length} nodes and ${updatedNewEdges.length} edges`
    );
    setCopiedFlow({ nodes: updatedNewNodes, edges: updatedNewEdges });
    dispatch(setSelectedNode());
  }, [
    nodes,
    edges,
    copiedFlow,
    dispatch,
    triggerElementsUpdate,
    isPageFocused,
    isViewOnlyMode,
    displayViewOnlyMessage,
  ]);

  const handleRemoveNodes = useCallback(
    (event) => {
      if (isViewOnlyMode) return displayViewOnlyMessage();
      const nodesId = event.map((node) => node.id);
      removeNode(nodesId);
    },
    [removeNode, isViewOnlyMode, displayViewOnlyMessage]
  );

  const handleCustomRemoveNodes = useCallback(() => {
    reactFlowInstance.deleteElements(selectedFlow);
  }, [selectedFlow, reactFlowInstance]);

  const handleViewOnlyMode = () => {
    setTooltipMessage(
      isViewOnlyMode ? "View Only Mode Disabled" : "View Only Mode Enabled"
    );
    setIsViewOnlyMode(!isViewOnlyMode);
  };

  useEffect(() => {
    const handleClickEvent = (e) => {
      const isFocused = componentRef.current.contains(e.target);
      setIsPageFocused(isFocused);
    };

    window.addEventListener(COPY, handleCopyNodes);
    window.addEventListener(PASTE, handlePasteNodes);
    window.addEventListener(CLICK, handleClickEvent);
    return () => {
      window.removeEventListener(CLICK, handleClickEvent);
      window.removeEventListener(COPY, handleCopyNodes);
      window.removeEventListener(PASTE, handlePasteNodes);
    };
  }, [handleCopyNodes, handlePasteNodes]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isPageFocused && event.ctrlKey) {
        switch (event.keyCode) {
          case undoKey:
            isViewOnlyMode ? displayViewOnlyMessage() : canUndo && undo();
            break;
          case redoKey:
            isViewOnlyMode ? displayViewOnlyMessage() : canRedo && redo();
            break;
          default:
            break;
        }
      }
    };
    window.addEventListener(KEYDOWN, handleKeyDown);

    return () => window.removeEventListener(KEYDOWN, handleKeyDown);
  }, [
    canUndo,
    canRedo,
    undo,
    redo,
    isPageFocused,
    isViewOnlyMode,
    displayViewOnlyMessage,
  ]);

  return (
    <div className="flex flex-col grow h-full" ref={componentRef}>
      <ReactFlowProvider>
        {showLeftSidebarButton && !isViewOnlyMode && (
          <div className="mr-auto">
            <img
              alt="chevron-right icon"
              className="bg-gray-500 hover:bg-gray-900 h-10 rounded-r-lg absolute z-10 w-3 "
              src={chevronRightIcon}
              onClick={() => {
                setLeftDrawerWidth(DEFAULT_DRAWER_WIDTH);
                setShowLeftSidebarButton(false);
              }}
            />
          </div>
        )}
        {showRightSidebarButton && (
          <div className="ml-auto w-3">
            <img
              alt="chevron-left icon"
              className="bg-gray-500 hover:bg-gray-900 h-10 rounded-l-lg absolute z-10 w-3"
              src={chevronLeftIcon}
              onClick={() => {
                setRightDrawerWidth(DEFAULT_DRAWER_WIDTH);
                setShowRightSidebarButton(false);
              }}
            />
          </div>
        )}
        <div className="h-full grow" ref={reactFlowWrapper}>
          {tooltipMessage && (
            <div className="absolute left-0 right-0 top-1 mx-auto w-max z-50 border rounded bg-gray-100 px-2 py-1 text-sm font-semibold">
              {tooltipMessage}
            </div>
          )}
          <Spinner name={FETCH_SERVICE}>
            {!isEmpty(nodes) ? (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={(event) => {
                  event[FIRST].type === REMOVE
                    ? handleRemoveNodes(event)
                    : !isViewOnlyMode && onNodesChange(event);
                }}
                onEdgesChange={(event) => {
                  event[FIRST].type === REMOVE
                    ? removeEdge(event, event[FIRST].id)
                    : onEdgesChange(event);
                }}
                className="validationflow !overflow-auto"
                onConnect={onConnect}
                onConnectEnd={onConnectEnd}
                isValidConnection={isValidConnection}
                connectionRadius={CONNECTION_RADIUS}
                onInit={onInit}
                onDrop={onDrop}
                onDragOver={onDragOver}
                fitView
                nodesDraggable={!isViewOnlyMode}
                nodesConnectable={!isViewOnlyMode}
                edgesFocusable={!isViewOnlyMode}
                nodeTypes={nodeTypes}
                proOptions={proOptions}
                deleteKeyCode={isPageFocused && DEFAULT_DELETE_KEY}
                onNodesDelete={() =>
                  !isViewOnlyMode && dispatch(setSelectedNode(null))
                }
                onNodeClick={() => dispatch(setSelectedModal(null))}
                onPaneClick={() => {
                  dispatch(setSelectedModal(null));
                  dispatch(setSelectedNode(null));
                }}
                onSelectionChange={(e) => {
                  if (isEqual(selectedFlow, e)) return;
                  setSelectedFlow(e);
                  e.nodes.length === ONE
                    ? dispatch(setSelectedNode(e.nodes[FIRST].data))
                    : dispatch(setSelectedNode());
                }}
                onPaneMouseMove={handleMouseMove}
                panOnScroll="true"
              >
                <Background variant={VARIANT} gap={GAP_VALUE} />
                <Controls
                  showInteractive={false}
                  className="flex flex-col-reverse"
                >
                  <ReactFlowToolbar
                    undo={undo}
                    redo={redo}
                    canUndo={canUndo}
                    canRedo={canRedo}
                    isFlowValidated={isFlowValidated}
                    handleCopyNodes={handleCopyNodes}
                    handlePasteNodes={handlePasteNodes}
                    copiedFlow={copiedFlow}
                    handleCustomRemoveNodes={handleCustomRemoveNodes}
                    isViewOnlyMode={isViewOnlyMode}
                    handleViewOnlyMode={handleViewOnlyMode}
                    displayViewOnlyMessage={displayViewOnlyMessage}
                  />
                </Controls>
              </ReactFlow>
            ) : (
              <div className=" place-content-center m-auto">
                {alertData && (
                  <Alert
                    type={alertData.type}
                    message={alertData.message}
                    handleOnClose={setAlertData}
                    timeout={8000}
                  />
                )}
              </div>
            )}
          </Spinner>
        </div>
      </ReactFlowProvider>
    </div>
  );
};

export default ReactFlowEditor;
